import { useCursorPager, useOffsetPager } from 'core/components/lib/Pagination'

export const PAGE_SIZE = 25

/**
 * How the endpoint pages. Use 'none' for endpoints that cannot page at all
 * (no cursor and no offset param): sending `limit` to one of those silently
 * truncates the list with no way to reach the rest.
 */
export type ResourcePagingModel = 'cursor' | 'offset' | 'none'

export type ResourcePagination = {
  total?: number
  count?: number
  nextUrl?: string | null
  previousUrl?: string | null
}

/**
 * Holds the pager state, so a resource component can hand `query` straight to
 * its own generated list hook and pass the rest to the view. Both pagers run
 * whatever the model is, which keeps the hook count the same for every
 * resource that calls this.
 */
const useResourceListPaging = <Query = { [param: string]: unknown }>(
  pagingModel: ResourcePagingModel,
) => {
  const cursorPager = useCursorPager()
  const offsetPager = useOffsetPager({ limit: PAGE_SIZE })

  const query =
    pagingModel === 'cursor'
      ? { limit: PAGE_SIZE, ...cursorPager.pageQuery }
      : pagingModel === 'offset'
      ? { limit: PAGE_SIZE, offset: offsetPager.offset }
      : {}

  // Absolute index of this page's first row, so the label reads "3–4 of 50"
  // rather than repeating the page size on every page.
  const rangeStart =
    pagingModel === 'cursor'
      ? cursorPager.pageIndex * PAGE_SIZE
      : offsetPager.offset

  const getPaginationProps = (pagination?: ResourcePagination) =>
    pagingModel === 'cursor'
      ? cursorPager.getPaginationProps(pagination)
      : offsetPager.getPaginationProps({
          count: pagination?.count,
          total: pagination?.total,
        })

  // The generated hook names its own query type; only the caller knows which.
  return { getPaginationProps, pagingModel, query: query as Query, rangeStart }
}

/** What the view needs. The query belongs to the resource, not to the view. */
export type ResourceListPaging = Omit<
  ReturnType<typeof useResourceListPaging>,
  'query'
>

export default useResourceListPaging
